import React from "react";
import { LiveFlightStatus as LiveStatus } from "hooks/useFlightStatus";

interface LiveFlightStatusProps {
  /** Live status from the Firestore listener, or null until a doc exists. */
  status: LiveStatus | null;
}

const fmtTime = (d?: Date): string =>
  d
    ? d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "—";

// Prefer the most authoritative time we have: actual > estimated > scheduled.
const bestTime = (
  actual?: Date,
  estimated?: Date,
  scheduled?: Date,
): { label: string; value: Date | undefined } => {
  if (actual) return { label: "Actual", value: actual };
  if (estimated) return { label: "Estimated", value: estimated };
  return { label: "Scheduled", value: scheduled };
};

const InfoCell: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) =>
  value ? (
    <div className="flex flex-col">
      <span className="text-lightgray-200 text-[10px]">{label}</span>
      <span className="text-grease-100 text-xs font-medium">{value}</span>
    </div>
  ) : null;

/**
 * Live flight-status panel. Purely presentational — the parent owns the
 * Firestore listener and passes the current status down (so the state badge in
 * the flight box and this panel share a single subscription). Renders nothing
 * until the backend publishes a doc, so the schedule above stays the source of
 * truth in the meantime.
 */
const LiveFlightStatus: React.FC<LiveFlightStatusProps> = ({ status }) => {
  if (!status) return null;

  const dep = bestTime(
    status.actualDeparture,
    status.estimatedDeparture,
    status.scheduledDeparture,
  );
  const arr = bestTime(
    status.actualArrival,
    status.estimatedArrival,
    status.scheduledArrival,
  );

  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      <div className="flex items-center gap-2 mb-2">
        <i className="st-flight-slim text-[#FFBC00] text-sm" />
        <span className="text-grease-100 text-xs font-semibold">
          Live Flight Status
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Departure */}
        <div className="border border-lightgray-100 rounded-md p-2 flex flex-col gap-1.5">
          <span className="text-lightgray-150 text-[10px] uppercase tracking-wide">
            Departure {status.source ? `· ${status.source}` : ""}
          </span>
          <div className="flex items-baseline gap-1">
            <span className="text-grease-100 text-sm font-semibold">
              {fmtTime(dep.value)}
            </span>
            <span className="text-lightgray-150 text-[10px]">{dep.label}</span>
          </div>
          <div className="flex gap-4">
            <InfoCell label="Terminal" value={status.departureTerminal} />
            <InfoCell label="Gate" value={status.departureGate} />
          </div>
        </div>

        {/* Arrival */}
        <div className="border border-lightgray-100 rounded-md p-2 flex flex-col gap-1.5">
          <span className="text-lightgray-150 text-[10px] uppercase tracking-wide">
            Arrival {status.destination ? `· ${status.destination}` : ""}
          </span>
          <div className="flex items-baseline gap-1">
            <span className="text-grease-100 text-sm font-semibold">
              {fmtTime(arr.value)}
            </span>
            <span className="text-lightgray-150 text-[10px]">{arr.label}</span>
          </div>
          <div className="flex gap-4">
            <InfoCell label="Terminal" value={status.arrivalTerminal} />
            <InfoCell label="Gate" value={status.arrivalGate} />
            <InfoCell label="Belt" value={status.arrivalBaggageBelt} />
          </div>
        </div>
      </div>

      {status.updatedAt && (
        <p className="text-lightgray-150 text-[10px] mt-2 text-right">
          Updated {fmtTime(status.updatedAt)}
        </p>
      )}
    </div>
  );
};

export default LiveFlightStatus;
